<script lang="ts">
import type { GlobalSingleRelationFieldId } from '#layers/data-model/types/schema/ids';
import type { CollectionFieldPath, CollectionFieldPathCollection, CollectionFieldPathForTerminal, CollectionFieldPathResolveOptions, CollectionFieldPathTerminal } from '#layers/data-model/utils/collection-field-path';
import type { FlattenedItem, NestedSelectMenuItem, NestedSelectMenuProps } from '#layers/ui/public';
import { flattenWithPaths } from '#layers/ui/public';

export interface CollectionFieldPathSelectAction {
	/** Stable action identifier emitted through the `action` event when selected. */
	id: string;
	/** Label shown in the menu and used for action search. */
	label: string;
	/** Additional searchable text for the action. */
	searchText?: string;
	/** Optional icon shown before the action label. */
	icon?: string;
	/** Disable the action without removing it from the menu. */
	disabled?: boolean;
}

export interface CollectionFieldPathSelectProps<TTerminal extends CollectionFieldPathTerminal | undefined = 'primitive'> {
	/** Collection whose primitive and relation fields are exposed as selectable field paths. */
	collection: CollectionFieldPathCollection;
	/** Include primitive fields constrained by another field. Defaults to excluding constrained fields. */
	includeConstrainedFields?: boolean;
	/** Include relation fields and their descendants. Defaults to true. */
	includeRelationFields?: boolean;
	/** Include to-many relation fields. Defaults to false. */
	includeListRelationFields?: boolean;
	/** Allow to-many relation fields as terminal selections without allowing traversal through them. Defaults to includeListRelationFields. */
	includeListRelationTerminalFields?: boolean;
	/** Include relations into system collections. Defaults to true. */
	includeSystemCollections?: boolean;
	/** Label shown for field items. Defaults to display names. */
	label?: 'displayName' | 'apiName';
	/** Sort order for root and nested field items. Defaults to label sorting. */
	order?: 'label' | 'schema';
	/** Controls whether relation fields can be selected as terminal paths. Defaults to primitive fields only. */
	terminal?: TTerminal;
	/** Resolves relation fields whose opposite collection is not embedded on the field object. */
	resolveRelationCollection?: (fieldId: GlobalSingleRelationFieldId) => CollectionFieldPathCollection | undefined;
	/** Return false to hide a selectable field path from the menu. */
	filterFieldPath?: (fieldPath: CollectionFieldPath) => boolean;
	/** Action commands prepended before field-path items. Actions emit `action` instead of selecting a field path. */
	leadingActions?: CollectionFieldPathSelectAction[];
	/** Maximum depth users can navigate in the nested field menu. */
	maxNestingDepth?: number;
	/** Maximum depth included in search results. */
	maxSearchDepth?: number;
	/** Placeholder shown in the nested menu search input. */
	searchPlaceholder?: string;
	/** Size forwarded to the nested select menu trigger/search controls. */
	size?: NestedSelectMenuProps['size'];
}
</script>

<script setup lang="ts" generic="TTerminal extends CollectionFieldPathTerminal | undefined = 'primitive'">
import { computed, useAttrs } from 'vue';
import { useDataModelDisplayNames } from '#layers/data-model/composables/use-data-model-display-names';
import { buildCollectionFieldPathItems, DEFAULT_FIELD_PATH_MAX_DEPTH, resolveCollectionFieldPathSelection, searchCollectionFieldPathItems } from '#layers/data-model/utils/collection-field-path';

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<CollectionFieldPathSelectProps<TTerminal>>(), {
	includeConstrainedFields: false,
	includeRelationFields: true,
	includeListRelationFields: false,
	includeListRelationTerminalFields: undefined,
	includeSystemCollections: true,
	leadingActions: () => [],
	label: 'displayName',
	order: 'label',
	maxNestingDepth: DEFAULT_FIELD_PATH_MAX_DEPTH,
	maxSearchDepth: 3,
	searchPlaceholder: undefined,
	size: undefined,
});
const emit = defineEmits<{
	'select': [fieldPath: CollectionFieldPathForTerminal<TTerminal>];
	'action': [actionId: string];
	'update:open': [open: boolean];
}>();

defineSlots<{
	default?: (props: { open: boolean }) => any;
}>();

const attrs = useAttrs();

const modelValue = defineModel<CollectionFieldPathForTerminal<TTerminal> | null>({ default: null });

const selectedPath = computed(() => modelValue.value ?? []);
const { getFieldDisplayName } = useDataModelDisplayNames();
const terminal = computed<CollectionFieldPathTerminal>(() => props.terminal ?? 'primitive');
const ACTION_ID_PREFIX = '__collection-field-path-action__:';

const fieldPathBuildOptions = computed(() => ({
	includeConstrainedFields: props.includeConstrainedFields,
	includeRelationFields: props.includeRelationFields,
	includeListRelationFields: props.includeListRelationFields,
	includeListRelationTerminalFields: props.includeListRelationTerminalFields,
	includeSystemCollections: props.includeSystemCollections,
	terminal: terminal.value,
	label: props.label,
	order: props.order,
	maxDepth: props.maxNestingDepth,
	resolveRelationCollection: props.resolveRelationCollection,
	getFieldDisplayName,
	filterFieldPath: props.filterFieldPath,
}));

const fieldPathResolveOptions = computed<CollectionFieldPathResolveOptions<TTerminal>>(() => ({
	includeConstrainedFields: props.includeConstrainedFields,
	includeRelationFields: props.includeRelationFields,
	includeListRelationFields: props.includeListRelationFields,
	includeListRelationTerminalFields: props.includeListRelationTerminalFields,
	includeSystemCollections: props.includeSystemCollections,
	terminal: props.terminal,
	resolveRelationCollection: props.resolveRelationCollection,
}));

const fieldPathItems = computed(() => buildCollectionFieldPathItems(props.collection, fieldPathBuildOptions.value));
const leadingActionItems = computed<NestedSelectMenuItem[]>(() => props.leadingActions.map((action) => toLeadingActionItem(action)));
const items = computed<NestedSelectMenuItem[][]>(() =>
	leadingActionItems.value.length > 0
		? [leadingActionItems.value, fieldPathItems.value]
		: [fieldPathItems.value],
);
const flattenedSearchFieldPathItems = computed(() => flattenWithPaths(fieldPathItems.value, props.maxSearchDepth));

function searchItems(query: string): FlattenedItem[] {
	return [
		...searchLeadingItems(flattenWithPaths(leadingActionItems.value, props.maxSearchDepth), query),
		...searchCollectionFieldPathItems(flattenedSearchFieldPathItems.value, query),
	];
}

function searchLeadingItems(items: FlattenedItem[], query: string): FlattenedItem[] {
	const normalizedQuery = query.trim().toLowerCase();

	if (!normalizedQuery) return [];

	const seenPaths = new Set<string>();

	return items.filter((entry) => {
		const searchableText = [entry.item.label, entry.item.searchText]
			.filter(Boolean)
			.join(' ')
			.toLowerCase();

		if (!searchableText.includes(normalizedQuery)) return false;

		const pathKey = entry.pathIds.join('\0');

		if (seenPaths.has(pathKey)) return false;

		seenPaths.add(pathKey);
		return true;
	});
}

interface LeadingActionMenuItem extends NestedSelectMenuItem {
	fieldPathSelectItemKind: 'action';
	actionId: string;
}

function toLeadingActionItem(action: CollectionFieldPathSelectAction): LeadingActionMenuItem {
	return {
		id: getLeadingActionMenuId(action.id),
		label: action.label,
		searchText: action.searchText,
		icon: action.icon,
		disabled: action.disabled,
		fieldPathSelectItemKind: 'action',
		actionId: action.id,
	};
}

function getLeadingActionMenuId(actionId: string) {
	return `${ACTION_ID_PREFIX}${actionId}`;
}

function getActionIdFromMenuSelection(item: NestedSelectMenuItem, path: string[]) {
	if (path.length !== 1) return null;
	if (!isLeadingActionMenuItem(item)) return null;

	return item.actionId;
}

function isLeadingActionMenuItem(item: NestedSelectMenuItem): item is LeadingActionMenuItem {
	const candidate = item as Partial<LeadingActionMenuItem>;

	return candidate.fieldPathSelectItemKind === 'action'
		&& typeof candidate.actionId === 'string';
}

function onSelect(item: NestedSelectMenuItem, path: string[]) {
	const actionId = getActionIdFromMenuSelection(item, path);

	if (actionId !== null) {
		emit('action', actionId);
		return;
	}

	const narrowedPath = resolveCollectionFieldPathSelection<TTerminal>(props.collection, path, fieldPathResolveOptions.value);

	if (!narrowedPath) return;
	if (props.filterFieldPath?.(narrowedPath) === false) return;

	modelValue.value = narrowedPath;
	emit('select', narrowedPath);
}
</script>

<template>
	<DNestedSelectMenu
		:model-value="selectedPath"
		:items
		:search-items
		:max-nesting-depth
		:max-search-depth
		:search-placeholder
		:size
		@select="onSelect"
		@update:open="emit('update:open', $event)"
	>
		<template v-if="$slots.default" #default="slotProps">
			<slot v-bind="{ ...attrs, ...slotProps }" />
		</template>
	</DNestedSelectMenu>
</template>
